import React, { useCallback, useEffect, useState } from 'react';
import { FolderOpen, Loader2 } from 'lucide-react';
import { archiveApi, UnlinkedFolders } from '../mocks/archiveApi';

/**
 * 연결 안 된 사업 폴더 — 드라이브 동기화가 찾은, 이어진 프로젝트가 없는 폴더.
 * 동기화는 프로젝트를 자동으로 만들지 않는다. 여기서 사람이 **잇거나 만든다**.
 * 목록이 비어 있으면 아무것도 그리지 않는다.
 */
export const ArchiveUnlinkedFolders: React.FC<{ refreshKey: number; onChanged: () => Promise<void> }> = ({ refreshKey, onChanged }) => {
  const [data, setData] = useState<UnlinkedFolders | null>(null);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await archiveApi.unlinkedFolders()); } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  // 마지막 폴더를 처리해도 결과 안내는 남긴다 — 전엔 칸째 사라져 성공 안내가 안 보였다(#113)
  if (!data || data.folders.length === 0) {
    return note ? (
      <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-800">
        {note} · 연결 안 된 폴더가 더 없습니다
        <button onClick={() => setNote(null)} className="text-emerald-500 hover:text-emerald-700">×</button>
      </div>
    ) : null;
  }

  const run = async (folderId: string, action: () => Promise<{ projectId: number; docs: number }>, label: string) => {
    setBusy(folderId);
    setNote(null);
    try {
      const r = await action();
      setNote(`${label} — 프로젝트 ${r.projectId} · 문서 ${r.docs}건을 붙였습니다`);
      await load();
      await onChanged();
    } catch (e) {
      setNote(`실패 — ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <FolderOpen size={15} /> 연결 안 된 사업 폴더 {data.folders.length}
        </span>
        <span className="text-xs text-amber-800">
          드라이브에 새로 생긴 사업 폴더입니다 — 기존 프로젝트에 잇거나 프로젝트로 만드세요 {open ? '▲' : '▼'}
        </span>
      </button>
      {open && (
        <div className="border-t border-amber-200 bg-white rounded-b-lg divide-y divide-slate-100">
          {data.folders.map((f) => (
            <div key={f.folderId} className="px-4 py-2.5 flex items-center gap-3 text-xs">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{f.name}</div>
                <div className="text-slate-400 truncate" title={f.folderPath}>{f.folderPath} · 파일 {f.docs}</div>
              </div>
              <select
                value={pick[f.folderId] ?? ''}
                onChange={(e) => setPick({ ...pick, [f.folderId]: Number(e.target.value) })}
                disabled={data.candidates.length === 0}
                className="w-56 px-2 py-1 border border-slate-200 rounded focus:border-indigo-400 focus:outline-none disabled:bg-slate-50"
              >
                <option value="">{data.candidates.length ? '이을 프로젝트 고르기' : '이을 프로젝트 없음'}</option>
                {data.candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.stage}</option>
                ))}
              </select>
              <button
                onClick={() => run(f.folderId, () => archiveApi.linkFolder(f.folderId, pick[f.folderId]), '이었습니다')}
                disabled={busy !== null || !pick[f.folderId]}
                className="px-2.5 py-1 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                잇기
              </button>
              <button
                onClick={() => run(f.folderId, () => archiveApi.createFolderProject(f.folderId), '만들었습니다')}
                disabled={busy !== null}
                title="앱에 없는 사업이면 — 과거 건처럼 프로젝트를 만든다"
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
              >
                {busy === f.folderId && <Loader2 size={11} className="animate-spin" />} 프로젝트로 만들기
              </button>
            </div>
          ))}
          <p className="px-4 py-2 text-[11px] text-slate-400">
            마지막 동기화 {data.syncedAt ? new Date(data.syncedAt).toLocaleString('ko-KR') : '—'}
            {' · '}잇기 목록은 드라이브 폴더가 없는 프로젝트(공고에서 담은 건)만 보입니다
            {note && <span className="ml-2 font-medium text-slate-700">{note}</span>}
          </p>
        </div>
      )}
    </div>
  );
};
